import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Activity, Compass, Handshake, Users, Calendar, FileText, Mic, FolderOpen,
  Brain, MessageSquare, DollarSign, Settings, Megaphone, MapPin, Clock, Video,
  User, CheckCircle, Loader2, Inbox,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  in_home_care: 'In-home care', telehealth_visit: 'Telehealth', wellness_visit: 'Wellness',
  companion_care: 'Companion care', post_discharge: 'Post-discharge', chronic_care: 'Chronic care',
  hospice_palliative: 'Hospice & palliative', mobile_urgent_care: 'Mobile urgent', behavioral_health: 'Behavioral health',
  skilled_nursing: 'Skilled nursing',
};
const URGENCY_LABELS = { routine: 'Routine', soon: 'Soon', same_day: 'Same day', urgent: 'Urgent' };

export default function ClinicianCareRequests() {
  const { getAuthHeader } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondTarget, setRespondTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/care-requests/open`, getAuthHeader());
      setRequests(data.care_requests || []);
    } catch (e) {
      toast.error('Could not load open care requests');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard' },
    { icon: Compass, label: 'Practice Coach', href: '/clinician/coach', highlight: true },
    { icon: Handshake, label: 'Offers Inbox', href: '/clinician/offers' },
    { icon: Megaphone, label: 'Care Requests', active: true },
    { icon: FileText, label: 'Clinical Notes', href: '/clinician/notes' },
    { icon: Mic, label: 'Voice Notes', href: '/clinician/voice-notes' },
    { icon: FolderOpen, label: 'Documents', href: '/clinician/documents' },
    { icon: MessageSquare, label: 'Messages', href: '/clinician/messages' },
    { icon: DollarSign, label: 'Earnings', href: '#' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Care requests near you"
      subtitle="Families have posted what they need. Respond with your interest and a proposed rate — win work without waiting for an offer."
      testId="clinician-care-requests-page"
    >
      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="cn-card p-6 h-40 animate-pulse bg-cn-bg/60" />)}</div>
      ) : requests.length === 0 ? (
        <div className="cn-card p-12 text-center bg-gradient-to-br from-white to-cn-bg/60" data-testid="clinician-care-requests-empty">
          <div className="w-20 h-20 rounded-3xl bg-cn-primary/10 text-cn-primary mx-auto flex items-center justify-center mb-4">
            <Inbox className="w-10 h-10" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text dark:text-white">No open requests right now</h3>
          <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">When families post a care need, it will show up here for you to respond to.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {requests.map((r) => (
            <div key={r.id} className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-6 flex flex-col" data-testid={`open-request-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="cn-display text-lg text-cn-text dark:text-white">{r.title}</h3>
                {r.budget_max && (
                  <div className="text-right flex-shrink-0">
                    <p className="font-display font-extrabold text-xl text-cn-primary leading-none">${r.budget_max}</p>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">budget/hr</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-cn-text-muted mt-1">Posted by {r.patient_name}</p>
              {r.description && <p className="text-sm text-cn-text mt-2">{r.description}</p>}
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {r.service_type && <Badge variant="outline" className="border-cn-border">{SERVICE_LABELS[r.service_type] || r.service_type}</Badge>}
                {r.state && <Badge variant="outline" className="border-cn-border"><MapPin className="w-3 h-3 mr-1" />{r.state}</Badge>}
                {r.visit_type && <Badge variant="outline" className="border-cn-border">{r.visit_type === 'telehealth' ? <Video className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}{r.visit_type.replace('_', ' ')}</Badge>}
                {r.urgency && <Badge variant="outline" className="border-cn-border"><Clock className="w-3 h-3 mr-1" />{URGENCY_LABELS[r.urgency] || r.urgency}</Badge>}
              </div>
              <div className="mt-auto pt-5">
                {r.already_responded ? (
                  <Button disabled className="cn-btn cn-btn-ghost w-full !py-2 text-cn-success" data-testid={`responded-${r.id}`}>
                    <CheckCircle className="w-4 h-4" /> You responded
                  </Button>
                ) : (
                  <Button onClick={() => setRespondTarget(r)} className="cn-btn cn-btn-primary w-full !py-2" data-testid={`respond-btn-${r.id}`}>
                    <Handshake className="w-4 h-4" /> Respond with interest
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <RespondModal
        request={respondTarget}
        open={!!respondTarget}
        onOpenChange={(v) => !v && setRespondTarget(null)}
        getAuthHeader={getAuthHeader}
        onResponded={(id) => setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, already_responded: true } : r)))}
      />
    </DashboardShell>
  );
}

const RespondModal = ({ request, open, onOpenChange, getAuthHeader, onResponded }) => {
  const [message, setMessage] = useState('');
  const [rate, setRate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setMessage(''); setRate(request?.budget_max ? String(request.budget_max) : ''); }
  }, [open, request]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/care-requests/${request.id}/respond`, {
        message: message || null,
        proposed_rate: rate ? Number(rate) : null,
      }, getAuthHeader());
      toast.success('Your interest was sent to the family');
      onResponded && onResponded(request.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send response');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="respond-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-cn-text">Respond to "{request?.title}"</DialogTitle>
          <DialogDescription className="text-cn-text-muted">Share why you're a great fit. The family will be notified and can book you directly.</DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Message to family</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. I have 8 years of dementia care experience and am available weekday mornings." className="mt-1.5 rounded-xl" rows={4} data-testid="respond-message" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Your proposed rate ($/hr)</Label>
            <Input type="number" min={1} value={rate} onChange={(e) => setRate(e.target.value)} placeholder="e.g. 75" className="mt-1.5 h-11 rounded-xl" data-testid="respond-rate" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={() => onOpenChange(false)} className="cn-btn cn-btn-ghost flex-1" data-testid="respond-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="cn-btn cn-btn-primary flex-1" data-testid="respond-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Handshake className="w-4 h-4" /> Send interest</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
